import { Box } from '@chakra-ui/react';
import { SearchInput, tools } from '@halo/ui-chat-react';
import {
  AccountType,
  GroupStatusEnum,
  isSessionCategory,
  SessionCategoryPrefix,
  StateEnum,
  useAppStore,
} from '@halo/chat-sdk-react';
import { ItemSelector } from './ItemSelector';

interface ChatsContentProps {
  resetTrigger: number;
  maxSelectedCount: number;
  selectChange: ({ chatIds }: { chatIds: string[] }) => void;
}

export function ChatsContent({
  resetTrigger,
  maxSelectedCount,
  selectChange,
}: ChatsContentProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const { sessions: allSessions, client } = useAppStore();
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<typeof allSessions>([]);
  const allSessionsRef = useRef<typeof allSessions>([]);

  const onSelectChat = useCallback(
    (chatId: string) => {
      setSelectedChatIds((prev) => {
        if (prev.includes(chatId)) {
          return prev.filter((id) => id !== chatId);
        }
        if (prev.length >= maxSelectedCount) {
          tools.toaster.error({
            title: t('tips.maxConversationsForwardCount', { count: maxSelectedCount }),
          });
          return prev;
        }
        return [...prev, chatId];
      });
    },
    [maxSelectedCount, t]
  );

  useEffect(() => {
    allSessionsRef.current = allSessions;
  }, [allSessions]);

  useEffect(() => {
    let isCancelled = false;
    const filterSessions = async () => {
      const syncFiltered = allSessionsRef.current.filter((session) => {
        const isSingle = isSessionCategory(session.chat_id, SessionCategoryPrefix.Single);
        const isGroup = isSessionCategory(session.chat_id, SessionCategoryPrefix.Group);
        if (isSingle) {
          const isDeleteAccount = session.user_info?.state === StateEnum.DELETE_ACCOUNT;
          const isMatch = session.name.toLowerCase().includes(searchValue.toLowerCase());
          return !isDeleteAccount && isMatch;
        }
        if (isGroup) {
          const isMatch = session.name.toLowerCase().includes(searchValue.toLowerCase());
          return isMatch;
        }
        return false;
      });
      const results = await Promise.all(
        syncFiltered.map(async (session) => {
          const isGroup = isSessionCategory(session.chat_id, SessionCategoryPrefix.Group);
          if (isGroup) {
            try {
              const groupInfo = await client?.Api.session.getGroupInfo(session.chat_id);
              return groupInfo?.status === GroupStatusEnum.Normal ||
                groupInfo?.status === GroupStatusEnum.LIMIT
                ? session
                : null;
            } catch {
              return null;
            }
          }
          return session;
        })
      );
      if (!isCancelled) {
        setFilteredSessions(
          results.filter(
            (session): session is NonNullable<typeof session> =>
              session !== null && session.user_info?.account_type !== AccountType.Robot
          )
        );
      }
    };
    filterSessions();
    return () => {
      isCancelled = true;
    };
  }, [client?.Api.session, searchValue]);

  const sessions = filteredSessions;

  useEffect(() => {
    selectChange({ chatIds: selectedChatIds });
  }, [selectedChatIds, selectChange]);

  useEffect(() => {
    if (resetTrigger) {
      setSelectedChatIds([]);
      setSearchValue('');
    }
  }, [resetTrigger]);

  return (
    <Box className="ChatsContent">
      <SearchInput
        inputProps={{
          bg: 'white',
        }}
        value={searchValue}
        onChange={(value) => setSearchValue(value)}
      />
      <Box
        className="ChatsContentList scrollHover"
        bg="white"
        borderRadius="lg"
        mt="3"
        px="2"
        py="1.5"
      >
        <Box>
          {sessions.map((session) => {
            const username = isSessionCategory(session.chat_id, SessionCategoryPrefix.Single)
              ? (session.user_info?.username ?? '')
              : '';

            return (
              <ItemSelector
                key={session.chat_id}
                id={session.chat_id}
                name={session.name}
                username={username}
                avatar={session.avatar}
                isChecked={selectedChatIds.includes(session.chat_id)}
                isGroup={isSessionCategory(session.chat_id, SessionCategoryPrefix.Group)}
                onClick={onSelectChat}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
